import type { ChatCard, ChatReactionChoice, ChatTaggableKind, GetChatMessageResponse } from '@orbit/api-types'

export type { ChatCard, ChatReactionChoice, ChatTaggableKind }

/** One Message of a trip's Chat, as the server sends it. */
export type ChatMessage = GetChatMessageResponse
export type ChatAuthor = ChatMessage['author']
export type ChatQuote = NonNullable<ChatMessage['quote']>
export type ChatPhoto = ChatMessage['photos'][number]
export type ChatReactionCount = ChatMessage['reactions'][number]
export type ChatLinkPreview = NonNullable<ChatMessage['linkPreview']>
/** Where one `@Name` sits in a Message's text, in UTF-16 units as JS strings count them. */
export type ChatMention = ChatCard['mentions'][number]
/** A tag's kind as the wire says it: a taggable kind, or `Other` for one added after this build. */
export type ChatTagKind = ChatCard['kind']
/** A reaction as the wire says it: one of the six, or `Other` for one added after this build. */
export type ChatReaction = ChatReactionCount['reaction']
/** Where a Message was written: the Chat itself, or a resource's Discussion of it. */
export type ChatMessageSource = 'chat' | 'discussion'

/** What a tag points at: a resource of its kind, or a member by their user id. */
export interface ChatTag {
	kind: ChatTagKind
	id: number
}

export const tagOf = (card: ChatTag): ChatTag => ({ kind: card.kind, id: card.id })

export const sameTag = (a: ChatTag, b: ChatTag): boolean => a.kind === b.kind && a.id === b.id

/** Every key fact empty, as the server fills a card of a kind they don't belong to. */
const NO_FACTS = {
	removed: false,
	inherited: false,
	people: [],
	mentions: [],
	legs: [],
	checkInDate: null,
	checkOutDate: null,
	date: null,
	amount: null,
	currencyCode: null,
	tripDayIndex: null,
	startTime: null,
	category: null,
	thumbnail: null,
} satisfies Omit<ChatCard, 'kind' | 'id' | 'title'>

/** A card built on this side, the way the server builds one, so it reads the same once sent. */
export const makeCard = (kind: ChatTagKind, id: number, title: string, facts: Partial<ChatCard> = {}): ChatCard => ({
	...NO_FACTS,
	kind,
	id,
	title,
	...facts,
})

/** A member as the server cards them: their first name, and just them as its people. */
export const memberCard = (member: ChatAuthor): ChatCard =>
	makeCard('Member', member.id, member.firstName, { people: [member] })

/** A photo picked for a Message, kept in the browser until its send uploads it. */
export interface OutgoingPhoto {
	/** The browser's id for it, not the server's. */
	id: string
	file: Blob
	/** An object URL the stream shows it from while the send is on its way. */
	url: string
	/** As displayed, in pixels. */
	width: number
	height: number
}

/**
 * What a send carries: the text, if any, the cards it tags (members with their
 * mentions), its photos, the Message it replies to, and where it was written. Kept
 * whole while the send is on its way, so the stream can show it and a retry can send it again.
 */
export interface OutgoingMessage {
	text: string | null
	/** Resources and members, in the order they were tagged. */
	cards: ChatCard[]
	/** In the order they show. */
	photos: OutgoingPhoto[]
	quote: ChatQuote | null
	source: ChatMessageSource
	/** False when the sender removed the preview of its first link, which then gets none. */
	previewsLink: boolean
}

/** A held Message, quoted to reply to it. */
export const quoteOfMessage = (message: ChatMessage): ChatQuote => ({
	id: message.id,
	author: message.author,
	text: message.text,
	tags: message.tags,
	removed: message.removed,
})
